import { Funnel } from "@phosphor-icons/react"
import {
  Button,
  FilterDrawer,
  FormGroup,
  FormQuerySelector,
  FormSelector,
  QuerySelector,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import { Dispatch, SetStateAction, useState } from "react"
import {
  useAllCommoditiesQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useAuthenticatedUserQuery,
  useStorageLocationSelectorQuery,
} from "~/generated/graphql"

export type StockFilter = {
  com_commodity?: any
  com_variety?: any
  com_grade?: any
  oth_status?: any
  oth_date_from?: any
  oth_date_to?: any
  oth_department?: any
  oth_storage_location?: any
}

type Props = {
  filter: StockFilter
  setFilter: Dispatch<SetStateAction<StockFilter>>
}

export const StockManagementFilters = ({ filter, setFilter }: Props) => {
  const [isOpen, setOpen] = useState(false)
  const [{ data }] = useAuthenticatedUserQuery()
  const departments = data?.me?.agent?.departments?.map(
    item => item?.department
  )

  function getAppliedFilters(prefix: string, filter: any) {
    const totalApplied = Object.keys(filter)?.filter?.(
      key => key.startsWith(prefix) && filter[key]
    )?.length
    return totalApplied ? `${totalApplied} Applied` : null
  }
  return (
    <>
      <Button
        variant={Object.values(filter)?.length > 0 ? "solid" : "outline"}
        className="whitespace-nowrap"
        onClick={() => setOpen(true)}
      >
        <Funnel />
      </Button>

      <FilterDrawer
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onApply={() => setOpen(false)}
        onClearAll={() => {
          setFilter({})
          setOpen(false)
        }}
        tabs={[
          {
            id: 1,
            title: "Commodity",
            subtitle: getAppliedFilters("com", filter),
            content: (
              <div className="flex flex-col gap-3">
                <FormGroup label="Commodity" className="w-full">
                  <QuerySelector
                    serverSideQuery
                    dataHook={useAllCommoditiesQuery}
                    value={filter.com_commodity}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_commodity: value,
                      }))
                    }
                  />
                </FormGroup>
                <FormGroup label="Variety" className="w-full">
                  <QuerySelector
                    dataHook={useAllVarietiesQuery}
                    variables={{
                      commodity_id: filter.com_commodity?.id,
                    }}
                    value={filter.com_variety}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_variety: value,
                      }))
                    }
                  />
                </FormGroup>
                <FormGroup label="Grade" className="w-full">
                  <QuerySelector
                    dataHook={useAllVarietyGradesQuery}
                    variables={{
                      variety: filter.com_variety?.id,
                    }}
                    value={filter.com_grade}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_grade: value,
                      }))
                    }
                  />
                </FormGroup>
              </div>
            ),
          },
          {
            id: 2,
            title: "Other",
            content: (
              <div className="flex flex-col gap-3">
                <FormSelector
                  label="Department"
                  options={departments}
                  value={filter.oth_department}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_department: value,
                    }))
                  }
                  isClearable={false}
                />
                <FormQuerySelector
                  label={"Storage Location"}
                  dataHook={useStorageLocationSelectorQuery}
                  className={"w-full"}
                  serverSideQuery
                  useWildCardsInQuery
                  value={filter.oth_storage_location}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_storage_location: value,
                    }))
                  }
                />
                <FormDateInput
                  label="Date From"
                  value={filter.oth_date_from}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_date_from: value,
                    }))
                  }
                />
                <FormDateInput
                  label="Date To"
                  value={filter.oth_date_to}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_date_to: value,
                    }))
                  }
                />
              </div>
            ),
          },
        ]}
      />
    </>
  )
}
